import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AiCategoryDefinition } from "@/components/dropdown/CategoryDropdown/CategoryDropdown.types";
import { brandSetBelongsToCategory } from "@/utils/aiVisibility/categories";

export type { AiCategoryDefinition };

export interface CategoriesState {
  categories: AiCategoryDefinition[];
  loaded: boolean;
}

const initialState: CategoriesState = {
  categories: [],
  loaded: false,
};

const categoriesSlice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    setCategories: (state, action: PayloadAction<AiCategoryDefinition[]>) => {
      state.categories = action.payload;
      state.loaded = true;
    },

    /**
     * Re-derive one brand set's category membership after it is created,
     * edited or deleted locally.
     *
     * Category-scoped screens (AI Visibility) filter the brand pool through
     * `category.brandIds`, which otherwise only ever changes on bootstrap. So
     * without this, a freshly created set is missing from every category and
     * stays unselectable until a page refresh.
     *
     * Pass `memberIds: []` to drop the set from every category (delete, or an
     * edit that turned it DISABLED).
     */
    syncBrandSetCategoryMembership: (
      state,
      action: PayloadAction<{ ulid: string; memberIds: (number | string)[] }>,
    ) => {
      const { ulid, memberIds } = action.payload;
      state.categories.forEach((category) => {
        const index = category.brandIds.findIndex(
          (brandId) => String(brandId) === ulid,
        );
        const shouldBelong = brandSetBelongsToCategory(category, memberIds);
        if (shouldBelong && index === -1) {
          category.brandIds.push(ulid);
        } else if (!shouldBelong && index !== -1) {
          category.brandIds.splice(index, 1);
        }
      });
    },
  },
});

export const { setCategories, syncBrandSetCategoryMembership } =
  categoriesSlice.actions;
export default categoriesSlice.reducer;
